import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../components/DashboardLayout";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import {
  formatDate,
  formatDateForInput,
  parseDateString,
} from "../utils/dateUtils";
import { createNotification } from "../utils/notifications";

function Lectures() {
  const { t } = useTranslation();
  const [lectures, setLectures] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [lectureToDelete, setLectureToDelete] = useState(null);
  const [viewingLecture, setViewingLecture] = useState(null);
  const [editingLecture, setEditingLecture] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const [formData, setFormData] = useState({
    title: "",
    instructorId: "",
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    notes: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [filterInstructor, setFilterInstructor] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [sortBy, setSortBy] = useState("newest-first");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;
  const [userRole, setUserRole] = useState(null);

  // Fetch user role
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserRole(userData.role || null);
          }
        } catch (error) {
          console.error("Error fetching user role:", error);
        }
      } else {
        setUserRole(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Check if user is admin
  const isAdmin = userRole === "admin";

  // Fetch lectures from Firestore
  const fetchLectures = async () => {
    try {
      const q = query(collection(db, "lectures"), orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      const lecturesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setLectures(lecturesData);
    } catch (error) {
      console.error("Error fetching lectures:", error);
      showToast(t("lectures.toast.fetchError"), "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch instructors from Firestore
  const fetchInstructors = async () => {
    try {
      const instructorsSnapshot = await getDocs(
        query(collection(db, "instructors"), orderBy("firstName", "asc"))
      );
      const instructorsData = instructorsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setInstructors(instructorsData);
    } catch (error) {
      console.error("Error fetching instructors:", error);
    }
  };

  useEffect(() => {
    fetchLectures();
    fetchInstructors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to get instructor name by ID
  const getInstructorNameById = (instructorId) => {
    if (!instructorId) return "";
    const instructor = instructors.find((i) => i.id === instructorId);
    return instructor ? `${instructor.firstName} ${instructor.lastName}` : "";
  };

  // Validation
  const validateField = (name, value) => {
    let error = "";

    const requiredFields = [
      "title",
      "startDate",
      "endDate",
      "dayOfWeek",
      "startTime",
      "endTime",
    ];

    if (requiredFields.includes(name) && !value) {
      error = t("lectures.validation.required");
    }

    // Date validation: end date should be after start date
    if (name === "endDate" && value && formData.startDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(value);
      if (endDate < startDate) {
        error = t("lectures.validation.endDateAfterStartDate");
      }
    }

    if (name === "startDate" && value && formData.endDate) {
      const startDate = new Date(value);
      const endDate = new Date(formData.endDate);
      if (endDate < startDate) {
        error = t("lectures.validation.endDateAfterStartDate");
      }
    }

    // Time validation: end time should be after start time
    if (name === "endTime" && value && formData.startTime) {
      const [startHour, startMin] = formData.startTime.split(":").map(Number);
      const [endHour, endMin] = value.split(":").map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      if (endMinutes <= startMinutes) {
        error = t("lectures.validation.endTimeAfterStartTime");
      }
    }

    if (name === "startTime" && value && formData.endTime) {
      const [startHour, startMin] = value.split(":").map(Number);
      const [endHour, endMin] = formData.endTime.split(":").map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      if (endMinutes <= startMinutes) {
        error = t("lectures.validation.endTimeAfterStartTime");
      }
    }

    return error;
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  // Handle input blur
  const handleBlur = (e) => {
    const { name, value } = e.target;

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      "title",
      "startDate",
      "endDate",
      "startTime",
      "endTime",
    ];

    requiredFields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    setTouched(
      requiredFields.reduce((acc, field) => ({ ...acc, [field]: true }), {})
    );

    return Object.keys(newErrors).length === 0;
  };

  // Show toast notification
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast(t("lectures.toast.fillRequired"), "error");
      return;
    }

    try {
      if (editingLecture) {
        // Update existing lecture
        const lectureRef = doc(db, "lectures", editingLecture.id);
        await updateDoc(lectureRef, {
          ...formData,
          updatedAt: serverTimestamp(),
        });

        await createNotification(
          "lecture_updated",
          "lecture",
          editingLecture.id,
          formData.title
        );
        showToast(t("lectures.toast.updated"), "success");
      } else {
        // Create new lecture
        const docRef = await addDoc(collection(db, "lectures"), {
          ...formData,
          createdAt: serverTimestamp(),
        });

        await createNotification(
          "lecture_added",
          "lecture",
          docRef.id,
          formData.title
        );
        showToast(t("lectures.toast.created"), "success");
      }

      // Refresh lectures list
      fetchLectures();
      handleCloseModal();
    } catch (error) {
      console.error("Error saving lecture:", error);
      showToast(t("lectures.toast.saveError"), "error");
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!lectureToDelete) return;

    try {
      await deleteDoc(doc(db, "lectures", lectureToDelete.id));
      await createNotification(
        "lecture_deleted",
        "lecture",
        lectureToDelete.id,
        lectureToDelete.title
      );
      showToast(t("lectures.toast.deleted"), "success");
      fetchLectures();
      setIsDeleteModalOpen(false);
      setLectureToDelete(null);
    } catch (error) {
      console.error("Error deleting lecture:", error);
      showToast(t("lectures.toast.deleteError"), "error");
    }
  };

  // Handle edit
  const handleEdit = (lecture) => {
    setEditingLecture(lecture);
    setFormData({
      title: lecture.title || "",
      instructorId: lecture.instructorId || "",
      startDate: lecture.startDate ? formatDateForInput(lecture.startDate) : "",
      endDate: lecture.endDate ? formatDateForInput(lecture.endDate) : "",
      startTime: lecture.startTime || "",
      endTime: lecture.endTime || "",
      notes: lecture.notes || "",
    });
    setIsModalOpen(true);
  };

  // Handle view
  const handleView = (lecture) => {
    setViewingLecture(lecture);
    setIsViewModalOpen(true);
  };

  // Handle add new
  const handleAddNew = () => {
    setEditingLecture(null);
    setFormData({
      title: "",
      instructorId: "",
      startDate: "",
      endDate: "",
      startTime: "",
      endTime: "",
      notes: "",
    });
    setErrors({});
    setTouched({});
    setIsModalOpen(true);
  };

  // Handle close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingLecture(null);
    setFormData({
      title: "",
      instructorId: "",
      startDate: "",
      endDate: "",
      startTime: "",
      endTime: "",
      notes: "",
    });
    setErrors({});
    setTouched({});
  };

  // Filter and search lectures
  const filteredAndSortedLectures = useMemo(() => {
    let filtered = [...lectures];

    // Search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (lecture) =>
          lecture.title?.toLowerCase().includes(searchLower) ||
          getInstructorNameById(lecture.instructorId)
            ?.toLowerCase()
            .includes(searchLower) ||
          lecture.notes?.toLowerCase().includes(searchLower)
      );
    }

    // Instructor filter
    if (filterInstructor) {
      filtered = filtered.filter(
        (lecture) => lecture.instructorId === filterInstructor
      );
    }

    // Date range filter (based on startDate)
    if (filterDateFrom || filterDateTo) {
      filtered = filtered.filter((lecture) => {
        const startDate = lecture.startDate
          ? new Date(lecture.startDate)
          : null;
        if (!startDate || isNaN(startDate.getTime())) return false;

        const lectureDate = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate()
        );

        if (filterDateFrom) {
          const fromDate = parseDateString(filterDateFrom);
          if (fromDate) {
            fromDate.setHours(0, 0, 0, 0);
            if (lectureDate < fromDate) return false;
          }
        }

        if (filterDateTo) {
          const toDate = parseDateString(filterDateTo);
          if (toDate) {
            toDate.setHours(23, 59, 59, 999);
            if (lectureDate > toDate) return false;
          }
        }

        return true;
      });
    }

    // Sorting
    filtered.sort((a, b) => {
      if (sortBy === "newest-first") {
        const aDate = a.createdAt
          ? a.createdAt.toDate
            ? a.createdAt.toDate()
            : new Date(a.createdAt.seconds * 1000)
          : new Date(0);
        const bDate = b.createdAt
          ? b.createdAt.toDate
            ? b.createdAt.toDate()
            : new Date(b.createdAt.seconds * 1000)
          : new Date(0);
        return bDate - aDate;
      } else if (sortBy === "oldest-first") {
        const aDate = a.createdAt
          ? a.createdAt.toDate
            ? a.createdAt.toDate()
            : new Date(a.createdAt.seconds * 1000)
          : new Date(0);
        const bDate = b.createdAt
          ? b.createdAt.toDate
            ? b.createdAt.toDate()
            : new Date(b.createdAt.seconds * 1000)
          : new Date(0);
        return aDate - bDate;
      } else if (sortBy === "title-az") {
        return (a.title || "").localeCompare(b.title || "");
      } else if (sortBy === "title-za") {
        return (b.title || "").localeCompare(a.title || "");
      } else if (sortBy === "startDate-newest") {
        const aDate = a.startDate ? new Date(a.startDate) : new Date(0);
        const bDate = b.startDate ? new Date(b.startDate) : new Date(0);
        return bDate - aDate;
      } else if (sortBy === "startDate-oldest") {
        const aDate = a.startDate ? new Date(a.startDate) : new Date(0);
        const bDate = b.startDate ? new Date(b.startDate) : new Date(0);
        return aDate - bDate;
      }
      return 0;
    });

    return filtered;
  }, [
    lectures,
    searchQuery,
    filterInstructor,
    filterDateFrom,
    filterDateTo,
    sortBy,
    instructors,
  ]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedLectures.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedLectures = filteredAndSortedLectures.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterInstructor, filterDateFrom, filterDateTo, sortBy]);

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery("");
    setFilterInstructor("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setSortBy("newest-first");
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Toast Notification */}
        {toast.show && (
          <div
            className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg ${
              toast.type === "success"
                ? "bg-green-500 text-white"
                : "bg-red-500 text-white"
            }`}
          >
            {toast.message}
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t("lectures.title")}
            </h1>
            <p className="text-gray-600 mt-1">{t("lectures.subtitle")}</p>
          </div>
          {isAdmin && (
            <button
              onClick={handleAddNew}
              className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              <span>{t("lectures.addLecture")}</span>
            </button>
          )}
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              placeholder={t("lectures.search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 pl-10 border-2 border-gray-400 rounded-lg bg-gray-50 font-semibold text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-black/25 focus:border-black text-base"
            />
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Instructor Filter */}
            <select
              value={filterInstructor}
              onChange={(e) => setFilterInstructor(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
            >
              <option value="">{t("lectures.filters.allInstructors")}</option>
              {instructors.map((instructor) => (
                <option key={instructor.id} value={instructor.id}>
                  {instructor.firstName} {instructor.lastName}
                </option>
              ))}
            </select>

            {/* Date From Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                {t("lectures.filters.dateFrom")}:
              </label>
              <input
                type="date"
                value={
                  filterDateFrom
                    ? formatDateForInput(
                        parseDateString(filterDateFrom) || filterDateFrom
                      )
                    : ""
                }
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    const date = new Date(value);
                    setFilterDateFrom(formatDate(date));
                  } else {
                    setFilterDateFrom("");
                  }
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
              />
            </div>

            {/* Date To Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                {t("lectures.filters.dateTo")}:
              </label>
              <input
                type="date"
                value={
                  filterDateTo
                    ? formatDateForInput(
                        parseDateString(filterDateTo) || filterDateTo
                      )
                    : ""
                }
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    const date = new Date(value);
                    if (filterDateFrom) {
                      const fromDate = parseDateString(filterDateFrom);
                      if (fromDate && date < fromDate) {
                        return;
                      }
                    }
                    setFilterDateTo(formatDate(date));
                  } else {
                    setFilterDateTo("");
                  }
                }}
                min={
                  filterDateFrom
                    ? formatDateForInput(
                        parseDateString(filterDateFrom) || filterDateFrom
                      )
                    : undefined
                }
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
              />
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
            >
              <option value="newest-first">
                {t("lectures.sort.newestFirst")}
              </option>
              <option value="oldest-first">
                {t("lectures.sort.oldestFirst")}
              </option>
              <option value="title-az">{t("lectures.sort.titleAZ")}</option>
              <option value="title-za">{t("lectures.sort.titleZA")}</option>
              <option value="startDate-newest">
                {t("lectures.sort.startDateNewest")}
              </option>
              <option value="startDate-oldest">
                {t("lectures.sort.startDateOldest")}
              </option>
            </select>

            {/* Clear Filters */}
            {(searchQuery ||
              filterInstructor ||
              filterDateFrom ||
              filterDateTo) && (
              <button
                onClick={handleClearFilters}
                className="ml-auto px-3 py-2 text-sm text-gray-600 hover:text-black hover:bg-gray-100 rounded-lg transition-colors flex items-center space-x-1"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                <span>{t("lectures.clearAll")}</span>
              </button>
            )}
          </div>
        </div>

        {/* Results Count */}
        {!loading && lectures.length > 0 && (
          <div className="flex items-center justify-between text-sm text-gray-600 px-2">
            <span>
              {t("lectures.showing")}{" "}
              <span className="font-semibold text-gray-900">
                {filteredAndSortedLectures.length > 0
                  ? `${startIndex + 1}-${Math.min(
                      startIndex + itemsPerPage,
                      filteredAndSortedLectures.length
                    )}`
                  : 0}
              </span>{" "}
              {t("lectures.of")}{" "}
              <span className="font-semibold text-gray-900">
                {filteredAndSortedLectures.length}
              </span>{" "}
              {t("lectures.lecturesText")}
              {filteredAndSortedLectures.length !== lectures.length && (
                <span className="text-gray-500">
                  {" "}
                  ({t("lectures.of")} {lectures.length} {t("lectures.total")})
                </span>
              )}
            </span>
          </div>
        )}

        {/* Lectures Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {filteredAndSortedLectures.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <svg
                className="w-16 h-16 mb-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
              <p>{t("lectures.table.noLectures")}</p>
              {isAdmin && (
                <button
                  onClick={handleAddNew}
                  className="mt-4 text-black hover:underline"
                >
                  {t("lectures.addFirstLecture")}
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("lectures.table.title")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("lectures.table.instructor")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("lectures.table.startDate")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("lectures.table.endDate")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("lectures.table.time")}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("lectures.table.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedLectures.map((lecture) => (
                    <tr key={lecture.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {lecture.title}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getInstructorNameById(lecture.instructorId) || (
                          <span className="text-gray-400 italic">
                            {t("lectures.table.notAssigned")}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {lecture.startDate
                          ? formatDate(lecture.startDate)
                          : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {lecture.endDate ? formatDate(lecture.endDate) : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {lecture.startTime && lecture.endTime
                          ? `${lecture.startTime} - ${lecture.endTime}`
                          : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-3">
                          <button
                            onClick={() => handleView(lecture)}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                            title={t("lectures.actions.view")}
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                          </button>
                          {isAdmin && (
                            <>
                              <button
                                onClick={() => handleEdit(lecture)}
                                className="text-indigo-600 hover:text-indigo-900 transition-colors"
                                title={t("lectures.actions.edit")}
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  />
                                </svg>
                              </button>
                              <button
                                onClick={() => {
                                  setLectureToDelete(lecture);
                                  setIsDeleteModalOpen(true);
                                }}
                                className="text-red-600 hover:text-red-900 transition-colors"
                                title={t("lectures.actions.delete")}
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && filteredAndSortedLectures.length > itemsPerPage && (
          <div className="flex items-center justify-center space-x-2 mt-4">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t("common.previous")}
            </button>

            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? "bg-black text-white border-black"
                        : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t("common.next")}
            </button>
          </div>
        )}

        {/* Add/Edit Lecture Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
              <div className="p-6 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingLecture
                    ? t("lectures.editLecture")
                    : t("lectures.form.title")}
                </h3>
              </div>

              <form
                onSubmit={handleSubmit}
                className="flex flex-col flex-1 overflow-hidden"
              >
                <div className="p-6 overflow-y-auto flex-1">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Title */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("lectures.form.titleLabel")}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={t("lectures.form.titlePlaceholder")}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                          errors.title ? "border-red-500" : "border-gray-300"
                        }`}
                      />
                      {errors.title && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.title}
                        </p>
                      )}
                    </div>

                    {/* Instructor */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("lectures.form.instructor")}
                      </label>
                      <select
                        name="instructorId"
                        value={formData.instructorId}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                      >
                        <option value="">
                          {t("lectures.form.selectInstructor")}
                        </option>
                        {instructors.map((instructor) => (
                          <option key={instructor.id} value={instructor.id}>
                            {instructor.firstName} {instructor.lastName}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Start Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("lectures.form.startDate")}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                          errors.startDate
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                      />
                      {errors.startDate && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.startDate}
                        </p>
                      )}
                    </div>

                    {/* End Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("lectures.form.endDate")}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        min={formData.startDate || undefined}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                          errors.endDate ? "border-red-500" : "border-gray-300"
                        }`}
                      />
                      {errors.endDate && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.endDate}
                        </p>
                      )}
                    </div>

                    {/* Start Time */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("lectures.form.startTime")}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="time"
                        name="startTime"
                        value={formData.startTime}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                          errors.startTime
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                      />
                      {errors.startTime && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.startTime}
                        </p>
                      )}
                    </div>

                    {/* End Time */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("lectures.form.endTime")}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="time"
                        name="endTime"
                        value={formData.endTime}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                          errors.endTime ? "border-red-500" : "border-gray-300"
                        }`}
                      />
                      {errors.endTime && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.endTime}
                        </p>
                      )}
                    </div>

                    {/* Notes */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("lectures.form.notes")}
                      </label>
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        rows={3}
                        placeholder={t("lectures.form.notesPlaceholder")}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                      />
                    </div>
                  </div>
                </div>

                <div className="p-6 border-t border-gray-200 flex justify-end space-x-3 flex-shrink-0">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    {t("lectures.form.cancel")}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
                  >
                    {editingLecture
                      ? t("lectures.form.updateLecture")
                      : t("lectures.form.createLecture")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* View Lecture Modal */}
        {isViewModalOpen && viewingLecture && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
                <h3 className="text-xl font-bold text-gray-900">
                  {t("lectures.view.title")}
                </h3>
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-500">
                      {t("lectures.view.title")}
                    </p>
                    <p className="mt-1 text-lg font-medium text-gray-900">
                      {viewingLecture.title}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">
                      {t("lectures.view.instructor")}
                    </p>
                    <p className="mt-1 text-sm text-gray-900">
                      {getInstructorNameById(viewingLecture.instructorId) || (
                        <span className="text-gray-400 italic">
                          {t("lectures.table.notAssigned")}
                        </span>
                      )}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">
                      {t("lectures.view.startDate")}
                    </p>
                    <p className="mt-1 text-sm text-gray-900">
                      {viewingLecture.startDate
                        ? formatDate(viewingLecture.startDate)
                        : "-"}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">
                      {t("lectures.view.endDate")}
                    </p>
                    <p className="mt-1 text-sm text-gray-900">
                      {viewingLecture.endDate
                        ? formatDate(viewingLecture.endDate)
                        : "-"}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">
                      {t("lectures.view.time")}
                    </p>
                    <p className="mt-1 text-sm text-gray-900">
                      {viewingLecture.startTime && viewingLecture.endTime
                        ? `${viewingLecture.startTime} - ${viewingLecture.endTime}`
                        : "-"}
                    </p>
                  </div>

                  {viewingLecture.notes && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-500">
                        {t("lectures.view.notes")}
                      </p>
                      <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                        {viewingLecture.notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end flex-shrink-0">
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
                >
                  {t("lectures.view.close")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && lectureToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                {t("lectures.delete.title")}
              </h3>
              <p className="text-gray-600 mb-6">
                {t("lectures.delete.message")}{" "}
                <span className="font-semibold text-gray-900">
                  {lectureToDelete.title}
                </span>
                ? {t("lectures.delete.cannotUndo")}
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setLectureToDelete(null);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  {t("lectures.delete.cancel")}
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  {t("lectures.delete.delete")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default Lectures;
